import React, { useState } from 'react';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  UserIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const NewConversationModal = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm.trim());
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState('');

  // Récupérer tous les utilisateurs pour la recherche
  const { data: usersData = { users: [], pagination: {} }, isLoading } = useQuery(
    ['all-users', debouncedSearch],
    async () => {
      // Recherche faite par le serveur (résultats bornés), plus sur une liste figée
      const response = await axios.get('/api/users/public', { params: { search: debouncedSearch } });
      return response.data;
    },
    { keepPreviousData: true }
  );

  // Extraire les utilisateurs
  const users = usersData.users || [];

  // Mutation pour créer une nouvelle conversation
  const createConversationMutation = useMutation(
    async (conversationData) => {
      const response = await axios.post('/api/messages/conversations', conversationData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('conversations');
        toast.success('Conversation créée avec succès !');
        onClose();
        setSelectedUser(null);
        setMessage('');
        setSearchTerm('');
      },
      onError: (error) => {
        console.error('Erreur création conversation:', error);
        toast.error('Erreur lors de la création de la conversation');
      }
    }
  );

  // Filtrer les utilisateurs selon la recherche
  const filteredUsers = users.filter(user => 
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchTerm(`${user.firstName} ${user.lastName}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedUser || !message.trim()) return;

    createConversationMutation.mutate({
      recipientId: selectedUser.id,
      content: message.trim()
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-text">Nouvelle conversation</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Recherche d'utilisateur */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rechercher un utilisateur
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Nom, email ou entreprise..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Liste des utilisateurs filtrés */}
              {searchTerm && (
                <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                  {isLoading ? (
                    <div className="p-4 text-center text-gray-500">
                      Chargement des utilisateurs...
                    </div>
                  ) : filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleUserSelect(user)}
                        className={`w-full text-left p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                          selectedUser?.id === user.id ? 'bg-primary-50' : ''
                        }`}
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                            <UserIcon className="h-5 w-5 text-primary-600" />
                          </div>
                          <div>
                            <p className="font-medium text-text">
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="text-sm text-gray-600">
                              {user.company && `${user.company} • `}
                              {user.role === 'entrepreneur' ? 'Entrepreneur' : 
                               user.role === 'investor' ? 'Investisseur' : 'Utilisateur'}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      Aucun utilisateur trouvé
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Utilisateur sélectionné */}
            {selectedUser && (
              <div className="bg-primary-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                    <UserIcon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-text">
                      {selectedUser.firstName} {selectedUser.lastName}
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedUser.company && `${selectedUser.company} • `}
                      {selectedUser.role === 'entrepreneur' ? 'Entrepreneur' : 
                       selectedUser.role === 'investor' ? 'Investisseur' : 'Utilisateur'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Message initial */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message initial
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="Écrivez votre message..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            {/* Boutons d'action */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={!selectedUser || !message.trim() || createConversationMutation.isLoading}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {createConversationMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Création...
                  </>
                ) : (
                  <>
                    <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                    Créer la conversation
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewConversationModal;

